/**
 * Mitarbeiter anlegen, Rolle und Status setzen, Zugänge vergeben.
 *
 * Der erste Bildschirm, ohne den nichts geht — jede Person beginnt hier.
 *
 * Die wichtigste Entscheidung im Aufbau: **ein Weg je Werkzeug.** Das
 * Backoffice öffnet man mit E-Mail und Passwort, die App mit einem
 * Einrichtungscode, der ein GERÄT an die Person bindet. Jedes Werkzeug hat
 * seine Spalte mit Zustand und Knopf (`MitarbeiterZeile.tsx`, HJ-010). Wer
 * was braucht, sagt der Seitentext je Rolle oben — ohne Testkonten, die sind
 * an der Zeile markiert.
 *
 * Code und Einmalpasswort stehen in einem Kasten, der stehen bleibt, bis
 * jemand ihn wegklickt (`MitarbeiterEinmalwerte.tsx`). Beides wird nur einmal
 * angezeigt; gespeichert ist jeweils nur ein Hash.
 *
 * Rolle und Status ändert nur ein Admin, in der Zeile, ohne Dialog — bei zehn
 * Personen ist ein Formular in einem Fenster mehr Weg als Nutzen. Die
 * Änderung geht über `mitarbeiter_rolle_status_setzen` (HJ-015): mit
 * gelesenem Stand, mit Eintrag in `kontoereignis`, und die Datenbank lässt
 * den letzten aktiven Admin nicht gehen.
 */

import { useEffect, useState } from 'react';
import { Seitenkopf } from '../Rahmen';
import { Fehlerkasten, Karte, Knopf, Ladehinweis, LeereListe } from '../gestaltung/bausteine';
import { useAbfrage } from '../daten/abfrage';
import { Speicherfehler } from '../daten/speichern';
import { alsKlarfehler, type Klarfehler } from '../daten/fehler';
import {
  codeZurueckziehen,
  einrichtungscodeErzeugen,
  mitarbeiterLaden,
  rolleStatusSetzen,
  ROLLENTEXTE,
  type Mitarbeiterzeile,
  type Rolle,
  type Status,
} from '../daten/mitarbeiter';
import { zugangVerwalten } from '../daten/zugang';
import { useSitzung } from '../anmeldung/Sitzung';
import {
  EinmalKasten,
  fristText,
  Sammelliste,
  type Einmalwert,
} from './MitarbeiterEinmalwerte';
import { AnlegenFormular, ZugangFormular } from './MitarbeiterFormulare';
import { MitarbeiterZeile } from './MitarbeiterZeile';
import { useFormularOffen } from '../daten/formularEntwurf';

export function MitarbeiterSeite() {
  const { stand } = useSitzung();
  const istAdmin = stand.art === 'bereit' && stand.mitarbeiter.rolle === 'admin';

  const liste = useAbfrage(mitarbeiterLaden, []);
  // Offen bleibt offen — auch über Seitenwechsel und Neuladen (HJ-025).
  const [formularOffen, setFormularOffen] = useFormularOffen('mitarbeiter-neu');
  const [arbeitsfehler, setArbeitsfehler] = useState<Klarfehler | null>(null);
  const [einmalwerte, setEinmalwerte] = useState<Einmalwert[]>([]);
  const [kopiert, setKopiert] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState<string | null>(null);
  const [zugangFuer, setZugangFuer] = useState<{
    person: Mitarbeiterzeile;
    art: 'anlegen' | 'passwort_neu';
  } | null>(null);

  /** Einen neuen Wert VORNE einreihen; ein älterer für dieselbe Sache fällt weg. */
  function einreihen(neu: Omit<Einmalwert, 'id'>) {
    setEinmalwerte((bisher) => [
      { ...neu, id: crypto.randomUUID() },
      ...bisher.filter((w) => w.fuer !== neu.fuer),
    ]);
    setKopiert(null);
  }

  // Solange ein Wert offen ist, warnt der Browser vor dem Verlassen: Danach ist
  // er weg, und nur noch ein neuer lässt sich erzeugen. Das deckt Neuladen und
  // Tab schließen ab — den Wechsel innerhalb der Anwendung nicht, und der wird
  // von der Sammelliste aufgefangen, die man vorher kopieren kann.
  useEffect(() => {
    if (einmalwerte.length === 0) return;
    const warnen = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warnen);
    return () => window.removeEventListener('beforeunload', warnen);
  }, [einmalwerte.length]);

  async function mitFehlerbehandlung(schluessel: string, tun: () => Promise<void>) {
    setArbeitsfehler(null);
    setBeschaeftigt(schluessel);
    try {
      await tun();
    } catch (problem) {
      setArbeitsfehler(
        problem instanceof Speicherfehler ? problem.klar : alsKlarfehler(problem),
      );
    } finally {
      setBeschaeftigt(null);
    }
  }

  async function codeErzeugen(person: Mitarbeiterzeile) {
    await mitFehlerbehandlung(`code:${person.id}`, async () => {
      const ergebnis = await einrichtungscodeErzeugen(person.id);
      const bis = new Date(ergebnis.gueltig_bis);
      const bisText = `${bis.toLocaleString('de-DE', {
        weekday: 'short',
        day: '2-digit',
        month: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      })} Uhr`;
      einreihen({
        fuer: `code:${person.id}`,
        ueberschrift: `Einrichtungscode für ${person.vorname} ${person.nachname}`,
        name: `${person.vorname} ${person.nachname}`,
        wert: ergebnis.code,
        frist: `bis ${bisText} (${fristText(bis)})`,
        nebenzeile: `Gültig bis ${bisText} — ${fristText(bis)} ab jetzt.`,
        erklaerung:
          'Gib ihn weiter, bevor du die Seite verlässt — er lässt sich nicht wieder aufrufen, nur neu erzeugen.',
      });
      liste.auffrischen();
    });
  }

  async function codeWeg(person: Mitarbeiterzeile) {
    await mitFehlerbehandlung(`weg:${person.id}`, async () => {
      await codeZurueckziehen(person.id);
      liste.auffrischen();
    });
  }

  async function zugangTun(
    person: Mitarbeiterzeile,
    art: 'anlegen' | 'passwort_neu',
    email?: string,
  ) {
    await mitFehlerbehandlung(`zugang:${person.id}`, async () => {
      const ergebnis = await zugangVerwalten(art, person.id, email);
      setZugangFuer(null);
      einreihen({
        fuer: `zugang:${person.id}`,
        ueberschrift:
          art === 'anlegen'
            ? `Zugang für ${ergebnis.name} angelegt`
            : `Neues Passwort für ${ergebnis.name}`,
        name: ergebnis.name,
        wert: ergebnis.passwort ?? '',
        frist: `Anmeldung mit ${ergebnis.email}, Passwort gilt nur einmal`,
        nebenzeile: `Anmeldung mit ${ergebnis.email}`,
        erklaerung:
          'Beim ersten Anmelden verlangt das Backoffice ein eigenes Passwort — dieses hier gilt nur einmal. Gib es weiter, bevor du die Seite verlässt.',
      });
      liste.auffrischen();
    });
  }

  // Aufgefrischt wird auch nach einem Fehler: Bei „Jemand war schneller" muss
  // der neue Stand in die Zeile, sonst scheitert der nächste Versuch genauso.
  async function rolleStatus(person: Mitarbeiterzeile, rolle: Rolle, status: Status) {
    await mitFehlerbehandlung(`feld:${person.id}`, async () => {
      try {
        await rolleStatusSetzen(person, rolle, status);
      } finally {
        liste.auffrischen();
      }
    });
  }

  return (
    <>
      <Seitenkopf
        titel="Mitarbeiter"
        erklaerung={
          <ul className="rollentexte">
            {ROLLENTEXTE.map(([rolle, text]) => (
              <li key={rolle}>
                <strong>{rolle}</strong> – {text}
              </li>
            ))}
          </ul>
        }
        werkzeuge={
          <Knopf art="haupt" onClick={() => setFormularOffen(!formularOffen)}>
            {formularOffen ? 'Abbrechen' : 'Mitarbeiter anlegen'}
          </Knopf>
        }
      />

      {/* Ab zwei Werten die Sammelliste: Wer für drei Personen nacheinander
          Codes erzeugt, will sie zusammen ausdrucken oder weitergeben — nicht
          dreimal einzeln abschreiben. */}
      {einmalwerte.length >= 2 && (
        <div style={{ marginBottom: 16 }}>
          <Sammelliste werte={einmalwerte} kopiert={kopiert} setzeKopiert={setKopiert} />
        </div>
      )}

      {einmalwerte.map((einmalwert) => (
        <div key={einmalwert.id} style={{ marginBottom: 16 }}>
          <EinmalKasten
            wert={einmalwert}
            schliessen={() =>
              setEinmalwerte((bisher) => bisher.filter((w) => w.id !== einmalwert.id))
            }
          />
        </div>
      ))}

      {arbeitsfehler && (
        <div style={{ marginBottom: 16 }}>
          <Fehlerkasten fehler={arbeitsfehler} />
        </div>
      )}

      {zugangFuer && (
        <div style={{ marginBottom: 16 }}>
          {/* key an der Person (E-027, H1.3a): Ohne ihn bleibt beim Wechsel
              in eine andere Zeile die getippte Adresse stehen, und das Konto
              entstünde mit der Anmeldeadresse der vorigen Person. Nur die ID —
              ein Neuaufbau bei jedem Auffrischen würfe die Eingabe weg. */}
          <ZugangFormular
            key={zugangFuer.person.id}
            person={zugangFuer.person}
            art={zugangFuer.art}
            laeuft={beschaeftigt === `zugang:${zugangFuer.person.id}`}
            abbrechen={() => setZugangFuer(null)}
            bestaetigen={(email) => void zugangTun(zugangFuer.person, zugangFuer.art, email)}
          />
        </div>
      )}

      {formularOffen && (
        <div style={{ marginBottom: 16 }}>
          <AnlegenFormular
            fertig={() => {
              setFormularOffen(false);
              liste.auffrischen();
            }}
          />
        </div>
      )}

      <Karte titel="Alle Mitarbeiter" blank>
        {liste.laedt && !liste.daten ? (
          <Ladehinweis />
        ) : liste.fehler ? (
          <div style={{ padding: 16 }}>
            <Fehlerkasten fehler={liste.fehler} nochmal={liste.auffrischen} />
          </div>
        ) : (
          <table className="tabelle">
            <thead>
              <tr>
                <th>Name</th>
                <th>Rolle</th>
                <th>Status</th>
                <th>Backoffice</th>
                <th>App</th>
              </tr>
            </thead>
            <tbody>
              {(liste.daten ?? []).length === 0 && (
                <LeereListe spalten={5}>
                  Noch niemand angelegt. Fang mit „Mitarbeiter anlegen" an.
                </LeereListe>
              )}
              {(liste.daten ?? []).map((person) => (
                <MitarbeiterZeile
                  key={person.id}
                  person={person}
                  istAdmin={istAdmin}
                  beschaeftigt={beschaeftigt}
                  codeErzeugen={() => void codeErzeugen(person)}
                  codeWeg={() => void codeWeg(person)}
                  zugangOeffnen={(art) => setZugangFuer({ person, art })}
                  rolleStatusSetzen={(rolle, status) => void rolleStatus(person, rolle, status)}
                />
              ))}
            </tbody>
          </table>
        )}
      </Karte>

      {!istAdmin && (
        <p className="meta" style={{ marginTop: 14, maxWidth: '68ch' }}>
          Rollen, Status und Bürozugänge ändert nur ein Admin. Ein Passwort für
          jemand anderen zu setzen heißt, sich danach als diese Person anmelden
          zu können — das ist keine Sachbearbeitung.
        </p>
      )}
    </>
  );
}
